import AppleLogo from '@kijiji/icons/src/icons/AppleLogo'
import GooglePlayLogo from '@kijiji/icons/src/icons/GooglePlayLogo'
import { useTranslation } from 'next-i18next'
import { useTheme } from 'styled-components'

import { HideAtOrLarger, ShowAtOrLarger } from '@/components/shared/breakpoint'
import { AppBannerQRCanvasLink } from '@/components/shared/global-footer/footer-app-banner/AppBannerQRCanvas'
import {
  FooterAppBannerContainer,
  FooterAppBannerImage,
  QRCodeContainer,
} from '@/components/shared/global-footer/footer-app-banner/styled'
import { TRANSLATION_KEYS } from '@/constants/localization'
import { ROUTE_LOCALE } from '@/domain/locale'
import { useLocale } from '@/hooks/useLocale'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { BodyText } from '@/ui/atoms/body-text'
import { Button } from '@/ui/atoms/button'
import { Flex } from '@/ui/atoms/flex'
import { HeadlineText } from '@/ui/atoms/headline-text'
import { getUserDevice } from '@/utils/getUserDevice'
import BannerImageEN from '~/public/next-assets/images/footer-banner-app-image-en.webp'
import BannerImageFR from '~/public/next-assets/images/footer-banner-app-image-fr.webp'

const FooterAppBanner = () => {
  const { colors, spacing } = useTheme()
  const { t } = useTranslation([TRANSLATION_KEYS.GLOBAL_FOOTER, TRANSLATION_KEYS.ROUTES])

  const { routeLocale } = useLocale()
  const { isAppleDevice, isPhoneOrTablet } = getUserDevice()

  const isFrench = routeLocale === ROUTE_LOCALE.fr

  const handleLinkClick = () => {
    trackEvent({ action: GA_EVENT.DownloadAppClick })
  }

  const storeConfig = {
    apple_store: {
      label: t('global_footer:app_banner.app_store.label'),
      href: t('routes:external.app_store.href'),
    },
    google_play: {
      label: t('global_footer:app_banner.google_play.label'),
      href: t('routes:external.google_play.href'),
    },
  }
  const currentDeviceStoreConfig = isAppleDevice ? storeConfig.apple_store : storeConfig.google_play

  return (
    <FooterAppBannerContainer>
      <FooterAppBannerImage
        alt={t('global_footer:app_banner.image.label')}
        srcImage={isFrench ? BannerImageFR : BannerImageEN}
        skipNextImage
      />

      <QRCodeContainer>
        <Flex as="section" flexDirection="column" gap={spacing.mini}>
          <HeadlineText
            as="p"
            color={colors.purple.primary}
            size={{ small: 'large', medium: 'giant' }}
            textAlign={{ small: 'left', medium: 'center' }}
          >
            {t('global_footer:app_banner.get_kijiji_app')}
          </HeadlineText>

          <BodyText
            color={colors.purple.primary}
            size={{ small: 'small', medium: 'xLarge' }}
            textAlign={{ small: 'left', medium: 'center' }}
          >
            {t('global_footer:app_banner.personalized_easy')}
          </BodyText>
        </Flex>

        <HideAtOrLarger breakpoint="medium">
          <Button
            as="a"
            variant="secondary"
            aria-label={currentDeviceStoreConfig.label}
            href={currentDeviceStoreConfig.href}
            onClick={handleLinkClick}
            rel="noopener noreferrer"
            target="_blank"
          >
            {t('global_footer:app_banner.use_the_app')}
          </Button>
        </HideAtOrLarger>

        <ShowAtOrLarger breakpoint="medium">
          <Flex as="section" flexWrap="wrap" justifyContent="center" gap={spacing.default}>
            <AppBannerQRCanvasLink
              LogoIcon={AppleLogo}
              handleClick={handleLinkClick}
              href={storeConfig.apple_store.href}
              linkAriaLabel={storeConfig.apple_store.label}
              linkLabel={t('global_footer:app_banner.see_on_app_store')}
              qrCodeDescription={t('global_footer:app_banner.scan_to_ios')}
              isPhoneOrTabletDevice={isPhoneOrTablet}
            />

            <AppBannerQRCanvasLink
              LogoIcon={GooglePlayLogo}
              handleClick={handleLinkClick}
              href={storeConfig.google_play.href}
              linkAriaLabel={storeConfig.google_play.label}
              linkLabel={t('global_footer:app_banner.see_on_play_store')}
              qrCodeDescription={t('global_footer:app_banner.scan_to_android')}
              isPhoneOrTabletDevice={isPhoneOrTablet}
            />
          </Flex>
        </ShowAtOrLarger>
      </QRCodeContainer>
    </FooterAppBannerContainer>
  )
}

export default FooterAppBanner
